import {
    computePredictedRemainingVolume,
    LiquiditySummaryData,
} from 'widgets/liquiditySummary/liquiditySummaryData';
import { stackData, getMax, SeriesPoint, Series } from 'utils/stackedSeries';

export const OrderSharesKey = 'order-shares';
export const OrderRemainingKey = 'order-rem';

export interface LiquiditySummaryChartItem {
    open: number;
    volume: number;
    remainingVolume: number;
    close: number;
}

export const initialChartItem: LiquiditySummaryChartItem = {
    open: 0,
    volume: 0,
    remainingVolume: 0,
    close: 0,
};

export interface LiquiditySummaryChartBar extends LiquiditySummaryChartItem {
    row: number;
}

const defaultLiquiditySummaryChartItem: LiquiditySummaryChartItem = {
    open: 0,
    volume: 0,
    remainingVolume: 0,
    close: 0,
};

export interface LiquiditySummaryChartData {
    historical: LiquiditySummaryChartItem;
    today: LiquiditySummaryChartItem;
    orderShares: number;
    orderRem: number;
    actualVsHistPct: number;
}

const defaultLiquiditySummaryChartData: LiquiditySummaryChartData = {
    historical: defaultLiquiditySummaryChartItem,
    today: defaultLiquiditySummaryChartItem,
    orderShares: 0,
    orderRem: 0,
    actualVsHistPct: 0,
};

export interface LiquiditySummaryChartSeriesData {
    maxX: number;
    series: Series<LiquiditySummaryChartBar, string>[];
}

export interface LiquiditySummaryChartSeriesPoint
    extends SeriesPoint<LiquiditySummaryChartBar> {
    key: string;
}

export function getChartData(
    data?: LiquiditySummaryData
): LiquiditySummaryChartData {
    if (!data) {
        return defaultLiquiditySummaryChartData;
    }
    // Calculate X values as per; confluence page spec https://confluence.dev.instinet.com/display/IN2/Liquidity+Summary+Data+Specification

    const histOpenX = data.histOpenVol ?? 0;
    const histTradedX = data.predictedTradedVolume ?? 0;
    const histPredictedX =
        (data.averageDailyVolume ?? 0) - (data.histCloseVol ?? 0);
    const histCloseX = data.averageDailyVolume ?? 0;

    const todayTradedX = data.volume ?? 0;

    const predictedRemainingVolume = computePredictedRemainingVolume(data);

    // predictedRemainingVolume includes the predictedClosingAuctionVolume so we deduct it here...
    const todayPredictedX =
        (data.volume ?? 0) +
        (predictedRemainingVolume ?? 0) -
        (data.predCloseVol ?? 0);
    const todayCloseX = (data.volume ?? 0) + (predictedRemainingVolume ?? 0);

    // If the data we need the widths
    return {
        historical: {
            open: histOpenX,
            volume: histTradedX - histOpenX,
            remainingVolume: histPredictedX - histTradedX,
            close: histCloseX - histPredictedX,
        },
        today: {
            open: 0,
            volume: todayTradedX,
            remainingVolume: todayPredictedX - todayTradedX,
            close: todayCloseX - todayPredictedX,
        },

        orderShares: data.orderQty - (data.orderLeaves ?? 0),
        orderRem: data.orderLeaves ?? 0,
        actualVsHistPct: data.actualVsHistPct ?? 0,
    };
}

export function getStackedChartData(
    chartData: LiquiditySummaryChartData
): LiquiditySummaryChartSeriesData {
    const barsData: LiquiditySummaryChartBar[] = [
        {
            row: 0,
            ...chartData.today,
        },
        {
            row: 1,
            ...chartData.historical,
        },
    ];

    const stackedSeries = stackData<LiquiditySummaryChartBar>(
        barsData,
        'open',
        'volume',
        'remainingVolume',
        'close'
    );

    return {
        series: stackedSeries,
        maxX: getMax(stackedSeries),
    };
}

export const isOrderKey = (key: string) =>
    key === OrderSharesKey || key === OrderRemainingKey;
